// ---------------------------------------------------------------------------
// The honest placeholder.
//
// Some screens appear in the menu because the employee's role will grant them
// access once they exist. Rather than opening an empty table, or worse, a page
// of buttons that quietly do nothing, this says plainly that the screen is not
// built, which phase it belongs to, and what it will contain.
//
// There is exactly one working control here: a link back. Nothing pretends.
// ---------------------------------------------------------------------------

import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, HardHat } from 'lucide-react';
import { useI18n } from '../i18n/I18nProvider';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useAuthStore } from '../stores/authStore';
import { landingPathFor, type NavItem } from '../routes/navigation';

export function NotBuiltYetPage({ item }: { item: NavItem }) {
  const { t, dir, format } = useI18n();
  const permissions = useAuthStore((state) => state.permissions);

  const title = t.nav[item.labelKey];
  useDocumentTitle(title);

  const phaseName = item.phase ? t.phases[item.phase] : '';
  const BackIcon = dir === 'rtl' ? ArrowRight : ArrowLeft;

  return (
    <div className="mx-auto max-w-2xl">
      <div className="card p-7 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-antique-gold/15 text-antique-gold">
          <HardHat className="h-7 w-7" aria-hidden="true" />
        </div>

        <h2 className="text-lg font-semibold text-ink">{title}</h2>
        <p className="mt-1.5 text-sm font-medium text-antique-gold">{t.phases.notBuiltHeading}</p>
        <p className="mx-auto mt-2 max-w-md text-sm text-ink-muted">
          {format(t.phases.notBuiltBody, { phase: phaseName })}
        </p>

        {item.plannedContent && item.plannedContent.length > 0 ? (
          <div className="mt-6 rounded-card border border-line bg-surface-soft p-5 text-start">
            <p className="mb-3 text-sm font-semibold text-ink">{t.phases.whatWillBeHere}</p>
            <ul className="space-y-2">
              {item.plannedContent.map((line) => (
                <li key={line} className="flex gap-2.5 text-sm text-ink-muted">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-butcher-green" aria-hidden="true" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <Link to={landingPathFor(permissions)} className="btn-secondary mt-6">
          <BackIcon className="h-4 w-4" aria-hidden="true" />
          {t.errors.goToStart}
        </Link>
      </div>
    </div>
  );
}
